import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Flame, Check, Calendar } from "lucide-react";

const BG_IMG = "https://images.unsplash.com/photo-1757308530438-4e2340a6475f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxNzkxNTczNjkwfDA&ixlib=rb-4.1.0&q=85";

export default function SevaClub() {
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({ donor_name: "", donor_email: "", gotra: "" });
  const [paying, setPaying] = useState(false);
  const [membership, setMembership] = useState(null);

  useEffect(() => { api.get("/seva-club/plan").then((r) => setPlan(r.data)); }, []);

  const checkMembership = async () => {
    if (!form.donor_email) return;
    const { data } = await api.get(`/seva-club/membership/${encodeURIComponent(form.donor_email.toLowerCase())}`);
    setMembership(data);
  };

  const join = async () => {
    if (!form.donor_name || !form.donor_email) return toast.error("Name and email are required");
    setPaying(true);
    try {
      const { data } = await api.post("/seva-club/checkout", { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setPaying(false); }
  };

  if (!plan) return <div className="p-10 text-[#63534E]">Loading…</div>;

  return (
    <div data-testid="seva-club-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A0E17]/85 via-[#4A0E17]/70 to-[#FAF6F0]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="overline text-[#D4AF37]">॥ नित्य आशीर्वाद मण्डलम् ॥</div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#FAF6F0] mt-3 leading-[1.05] max-w-3xl">
            The Daily Blessing Club
          </h1>
          <p className="mt-5 text-lg text-[#FAF6F0]/90 max-w-2xl leading-relaxed">
            One offering of <span className="font-mono text-[#D4AF37]">${plan.amount_usd}</span>
            {" "}brings {plan.cycle_days} days of daily seva in your name at Pancham Dham.
            A gentle reminder arrives before each cycle ends so your blessings continue uninterrupted.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-14 relative z-10 pb-20">
        <div className="grid lg:grid-cols-5 gap-7">
          <div className="lg:col-span-3 bg-[#FFFDF9] sacred-border rounded-3xl p-9 relative om-watermark">
            <Flame className="w-10 h-10 text-[#E65100]" />
            <h2 className="font-serif text-3xl text-[#4A0E17] mt-5">What your membership performs</h2>
            <ul className="mt-6 space-y-3">
              {plan.benefits.map((b, i) => (
                <li key={i} className="flex gap-3 text-[#1F1412]">
                  <Check className="w-5 h-5 text-[#E65100] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-xl p-5 text-sm text-[#4A0E17]">
              <strong>How it works:</strong> This is a monthly commitment via a single $108 offering.
              Fifteen days before your cycle ends, we send a gentle renewal note so your lamp burns without interruption.
            </div>
          </div>

          <aside className="lg:col-span-2 bg-[#4A0E17] text-[#FAF6F0] rounded-3xl p-8 relative overflow-hidden">
            <div className="absolute -top-10 -right-5 font-devanagari text-[220px] leading-none text-[#D4AF37] opacity-10 pointer-events-none">ॐ</div>
            <div className="overline text-[#D4AF37]">Join the club</div>
            <div className="font-mono text-5xl mt-2 font-semibold">${plan.amount_usd}</div>
            <div className="text-sm text-[#F3E5AB]/80">for the next {plan.cycle_days} days of daily seva</div>
            <div className="mt-6 space-y-3">
              <Input data-testid="seva-club-name" placeholder="Full name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className="bg-white text-[#1F1412] border-0 h-11" />
              <Input data-testid="seva-club-email" type="email" placeholder="Email" value={form.donor_email} onBlur={checkMembership} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} className="bg-white text-[#1F1412] border-0 h-11" />
              <Input data-testid="seva-club-gotra" placeholder="Gotra (optional)" value={form.gotra} onChange={(e) => setForm({ ...form, gotra: e.target.value })} className="bg-white text-[#1F1412] border-0 h-11" />
            </div>
            {membership?.active && (
              <div className="mt-5 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-xl p-4 text-sm">
                <div className="flex items-center gap-2 text-[#F3E5AB] font-semibold">
                  <Calendar className="w-4 h-4" /> Active member
                </div>
                <div className="text-[#F3E5AB]/80 mt-1">Next renewal: <span className="font-mono">{membership.next_renewal}</span></div>
                <div className="text-[#F3E5AB]/70 text-xs mt-2">Pay again to extend by {plan.cycle_days} days.</div>
              </div>
            )}
            <Button data-testid="seva-club-join" disabled={paying} onClick={join} className="mt-6 w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12 text-base">
              {paying ? "Redirecting…" : (membership?.active ? "Renew membership" : "Join the club")}
            </Button>
            <p className="text-xs text-[#F3E5AB]/70 mt-4 leading-relaxed">
              Secure checkout via Stripe. Membership emails arrive at your inbox. No account required.
            </p>
          </aside>
        </div>
      </section>
    </div>
  );
}
